import { PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from 'react';

import * as searchApi from '@/features/search/api';
import { buildSearchRequest, parseQuery } from '@/features/search/query';
import { Item } from '@/features/backlog/types';
import { priorityMeta, taskTypeMeta } from '@/features/backlog/constants';
import { useAppData } from '@/app-data';
import { useIdentity } from '@/features/identity/identity';
import { taskPath } from '@/constants/routes';
import { CanvasElement } from '@/features/canvas/types';
import { TaskTypeGlyph, PriorityChevron, Search, ExternalLink } from '@/components/ui/icons';
import MemberAvatar from '@/components/member-avatar';
import { useCanvasItems } from './use-canvas-items';
import { fieldOn } from './card-fields';

const stop = (e: ReactPointerEvent) => e.stopPropagation();
const humanize = (v: string) => v.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x));
const POLL_MS = 25000; // fallback refresh so a missed fan-out still self-heals

/**
 * A live board-search results table on the canvas (SBB-14). Runs the element's SparkQL query and renders
 * the matching items; re-runs whenever a fanned item change lands (changeTick) — plus a periodic poll and
 * a manual refresh — so the table stays live. It caches the boards its results span back onto the element
 * (style.board_ids) so item mutations on those boards fan into this whiteboard.
 */
export default function SearchCard({
  element,
  onAutoHeight,
  onSetBoards,
}: {
  element: CanvasElement;
  onAutoHeight?: (px: number) => void;
  onSetBoards?: (boardIds: string[]) => void;
}) {
  const { boards, members } = useAppData();
  const { me } = useIdentity();
  const { boardsById, changeTick } = useCanvasItems();
  const query = element.style?.query ?? '';
  const [results, setResults] = useState<Item[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const rootRef = useRef<HTMLDivElement>(null);
  const seq = useRef(0);
  const memberById = new Map(members.map((m) => [m.id, m]));
  // The poll/timeout are created in an effect keyed only on [query, changeTick], so their `run` closure can
  // outlive several board_ids writes. Read the LATEST board_ids via a ref so the sameSet guard compares
  // against current truth (not the mount-time capture) and never re-calls onSetBoards for an unchanged set.
  const boardIdsRef = useRef<string[] | undefined>(element.style?.board_ids);
  boardIdsRef.current = element.style?.board_ids;

  // Report natural height (no clip / scrollbar) so the element box grows to fit.
  useEffect(() => {
    const node = rootRef.current;
    if (!node || !onAutoHeight) return undefined;
    const ro = new ResizeObserver(() => onAutoHeight(node.offsetHeight));
    ro.observe(node);
    onAutoHeight(node.offsetHeight);
    return () => ro.disconnect();
  }, [onAutoHeight]);

  const run = () => {
    const s = ++seq.current;
    setLoading(true);
    const req = buildSearchRequest(parseQuery(query), { boards, members, meId: me?.id }, 50);
    searchApi
      .searchItems(req)
      .then((r) => {
        if (s !== seq.current) return;
        setResults(r.data);
        setTotal(r.total);
        // Keep the fan-out coverage current: the DISTINCT boards the FULL match set spans (server-provided,
        // so matches beyond the returned page still fan in). Fall back to the page's boards on older servers.
        const spanned = r.board_ids ?? Array.from(new Set(r.data.map((it) => it.board_id)));
        if (onSetBoards && !sameSet(spanned, boardIdsRef.current ?? [])) onSetBoards(spanned);
      })
      .catch(() => { if (s === seq.current) { setResults([]); setTotal(0); } })
      .finally(() => { if (s === seq.current) setLoading(false); });
  };

  // Re-run on mount, query change, and any applied item change (debounced), plus a periodic poll.
  useEffect(() => {
    const t = window.setTimeout(run, 120);
    const poll = window.setInterval(run, POLL_MS);
    return () => { window.clearTimeout(t); window.clearInterval(poll); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, changeTick]);

  const openInNewTab = (it: Item) => {
    const b = boardsById.get(it.board_id);
    if (b) window.open(taskPath(b.slug, it.key), '_blank', 'noopener,noreferrer');
  };

  const on = (k: string) => fieldOn(element.style, k);

  return (
    <div ref={rootRef} className="flex w-full flex-col rounded-card border border-line bg-surface-raised shadow">
      <div className="flex items-center gap-2 border-b border-line px-2.5 py-1.5">
        <Search className="h-3.5 w-3.5 shrink-0 text-fg-faint" />
        <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-fg-soft" title={query || 'All items'}>{query || 'All items'}</span>
        <span className="shrink-0 tnum text-[10px] text-fg-faint">{total}</span>
        <button type="button" onPointerDown={stop} onClick={run} title="Refresh" className="shrink-0 text-fg-ghost transition-colors hover:text-fg">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-2.64-6.36M21 3v6h-6" /></svg>
        </button>
      </div>
      {results.length === 0 ? (
        <div className="px-3 py-6 text-center text-[11px] text-fg-faint">{loading ? 'Searching…' : 'No matching items.'}</div>
      ) : (
        <div>
          {results.map((it) => {
            const pm = priorityMeta(it.priority);
            const labels = it.labels ?? [];
            return (
            <div key={it.id} className="flex items-center gap-1.5 border-b border-line/60 px-2.5 py-1 last:border-0">
              {on('type') ? <TaskTypeGlyph type={it.type} color={taskTypeMeta(it.type).color} /> : null}
              {on('key') ? <span className="item-key shrink-0 rounded px-1 py-px tnum text-[9px] font-semibold">{it.key}</span> : null}
              {on('priority') ? <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center" title={pm.label}><PriorityChevron level={pm.level} color={pm.color} /></span> : null}
              <button type="button" onPointerDown={stop} onClick={() => openInNewTab(it)} className="min-w-0 flex-1 truncate text-left text-[11px] text-fg-soft hover:text-fg" title={it.title}>
                {it.title || 'Untitled'}
              </button>
              {on('labels') && labels.length > 0 ? (
                <span className="hidden shrink-0 items-center gap-0.5 sm:flex" title={labels.join(', ')}>
                  {labels.slice(0, 2).map((l) => <span key={l} className="rounded-full bg-layer px-1 py-px text-[8.5px] font-medium text-fg-faint">{l}</span>)}
                </span>
              ) : null}
              {on('status') ? <span className="hidden shrink-0 truncate text-[9.5px] text-fg-faint sm:inline" title={humanize(it.status)}>{humanize(it.status)}</span> : null}
              {on('assignee') ? <MemberAvatar member={it.assignee_id ? memberById.get(it.assignee_id) : undefined} size={16} className="shrink-0" /> : null}
              <button type="button" onPointerDown={stop} onClick={() => openInNewTab(it)} title="Open" className="shrink-0 text-fg-ghost transition-colors hover:text-fg"><ExternalLink className="h-3 w-3" /></button>
            </div>
            );
          })}
          {total > results.length ? <div className="px-2.5 py-1 text-center text-[9.5px] text-fg-ghost">+{total - results.length} more</div> : null}
        </div>
      )}
    </div>
  );
}
